import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Send, CreditCard, Receipt, AlertCircle, Phone, Mail, MapPin, FileText, Car, Clock, ChevronDown, Calendar, DollarSign, Eye } from 'lucide-react';
import InvoiceDisplay from './InvoiceDisplay';
import { voidPayment } from '../lib/payments';
import { useBusiness } from '../contexts/BusinessContext';
import html2pdf from 'html2pdf.js';
import { sendCustomMessage } from '../lib/messaging';
import { sendEmailWithFallback } from '../lib/email';
import { supabase } from '../lib/supabase';
import { getBooking } from '../lib/bookings';
import { getSiteUrl } from '../lib/siteConfig';



interface ServiceAddon {
  id: string;
  name: string;
  description?: string;
  price: number;
  duration_minutes?: number;
}

interface BookingService {
  id: string;
  name: string;
  description: string | null;
  price: number;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_name?: string;
  tax_exempt?: boolean;
  tax_rate?: number;
  add_ons?: ServiceAddon[];
}

interface PaymentDetails {
  id: string;
  booking_id: string;
  business_id: string;
  amount: number;
  tip_amount?: number;
  base_amount?: number;
  status: 'pending' | 'completed' | 'voided';
  created_at: string;
  notes?: string;
  pdf_url?: string;
  booking_number: number;
  booking_title?: string;
  booking_subtotal?: number;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  booking_services?: BookingService[];
  booking_discounts?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
}

interface PaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  allPayments: PaymentDetails[];  // Changed: now accepts all payments for the booking
  onUpdate: () => Promise<void>;
}

export default function PaymentDetailsModal({ 
  isOpen, 
  onClose, 
  bookingId,
  allPayments,
  onUpdate
}: PaymentDetailsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [sendOptionsOpen, setSendOptionsOpen] = useState(false);
  const [isSendingText, setIsSendingText] = useState(false);
  const [textSentSuccess, setTextSentSuccess] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDetails | null>(null);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const sendMenuRef = useRef<HTMLDivElement>(null);
  
  // Add state to track local payments for immediate updates
  const [localAllPayments, setLocalAllPayments] = useState<PaymentDetails[]>(allPayments);

  // Update local payments when props change
  useEffect(() => {
    setLocalAllPayments(allPayments);
  }, [allPayments]);

  // Initialize selected payment based on business logic
  useEffect(() => {
    if (localAllPayments.length === 0) return;

    // Find the most relevant invoice to display by default
    const unpaidInvoices = localAllPayments.filter(p => p.status === 'pending' || p.id === 'pending');
    const completedPayments = localAllPayments.filter(p => p.status === 'completed');
    const voidedPayments = localAllPayments.filter(p => p.status === 'voided');

    let defaultPayment: PaymentDetails;

    if (unpaidInvoices.length > 0) {
      // Show the most recent unpaid invoice
      defaultPayment = unpaidInvoices.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    } else if (completedPayments.length > 0) {
      // Show the most recent completed payment
      defaultPayment = completedPayments.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    } else {
      // Show the most recent payment (could be voided)
      defaultPayment = localAllPayments.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    }

    setSelectedPayment(defaultPayment);
  }, [localAllPayments]);

  // Check if payment is pending
  const isPending = selectedPayment?.status === 'pending' || selectedPayment?.id === 'pending';
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'credit_card':
        return <CreditCard className="h-4 w-4 text-blue-500" />;
      case 'bank_transfer':
        return <Receipt className="h-4 w-4 text-green-500" />;
      default:
        return <CreditCard className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (!invoiceRef.current) return;
      
      setIsDownloading(true);
      
      // Create a clone of the invoice element to modify for PDF
      const element = invoiceRef.current.cloneNode(true) as HTMLElement;
      
      // Remove any buttons or interactive elements from the PDF
      const buttonsToRemove = element.querySelectorAll('button');
      buttonsToRemove.forEach(button => button.remove());
      
      // Add a PDF-specific class for styling
      element.classList.add('pdf-invoice');
      
      // Create a temporary container with proper styling for PDF generation
      const container = document.createElement('div');
      container.appendChild(element);
      container.style.width = '210mm'; // A4 width
      container.style.padding = '10mm';
      container.style.backgroundColor = 'white';
      
      // Make sure stamps are visible in PDF
      const stamps = container.querySelectorAll('[class*="rotate"]');
      stamps.forEach(stamp => {
        const stampEl = stamp as HTMLElement;
        stampEl.style.position = 'absolute';
        stampEl.style.zIndex = '5';
        stampEl.style.opacity = '0.3';
      });
      
      // Ensure table layouts are preserved
      const tables = container.querySelectorAll('table');
      tables.forEach(table => {
        table.style.width = '100%';
        table.style.borderCollapse = 'collapse';
      });
      
      // Add business information and invoice title on top
      const headerText = document.createElement('div');
      headerText.style.textAlign = 'center';
      headerText.style.marginBottom = '20px';
      headerText.innerHTML = `<h1 style="font-size: 24px; color: #333; margin-bottom: 5px;">INVOICE</h1>
                              <p style="margin: 0; color: #666;">${selectedBusiness?.name || 'Auto Service'}</p>`;
      container.insertBefore(headerText, container.firstChild);
      
      // Add footer with page number
      const footer = document.createElement('div');
      footer.style.textAlign = 'center';
      footer.style.marginTop = '20px';
      footer.style.fontSize = '10px';
      footer.style.color = '#999';
      footer.innerHTML = 'Thank you for your business';
      container.appendChild(footer);
      
      // Set options for the PDF
      const options = {
        margin: [5, 0, 5, 0],
        filename: `Invoice-${selectedPayment?.booking_number || 'Receipt'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          letterRendering: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' as 'portrait',
          compress: true
        }
      };
      
      // Generate and download the PDF
      await html2pdf().from(container).set(options).save();
      
    } catch (err) {
      console.error('Error generating PDF:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  // Close send options dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sendMenuRef.current && !sendMenuRef.current.contains(event.target as Node)) {
        setSendOptionsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Generate a public receipt URL for sharing
  const generateReceiptUrl = async () => {
    try {
      if (!selectedPayment) {
        console.error('Missing payment data');
        return null;
      }
      
      if (!supabase) {
        console.error('Supabase client is not initialized');
        return null;
      }
      
      // Use PostgreSQL to generate a secure hex token like the proposal system
      console.log('🔐 Generating secure receipt token via PostgreSQL...');
      const { data: tokenData, error: tokenError } = await supabase
        .rpc('generate_receipt_token', {
          p_payment_id: selectedPayment.id,
          p_booking_id: selectedPayment.booking_id,
          p_business_id: selectedPayment.business_id
        });

      if (tokenError) {
        console.error('Error generating receipt token:', tokenError);
        return null;
      }

      if (!tokenData) {
        console.error('No token returned from generate_receipt_token');
        return null;
      }

      console.log('✅ Secure token generated:', tokenData);
      
      // Determine the base URL
      const baseUrl = getSiteUrl();
      
      // Create the receipt URL using the same format as proposals
      const receiptUrl = `${baseUrl}/receipt/${tokenData}`;
      
      console.log('📄 Generated Receipt URL:', receiptUrl);
      return receiptUrl;
      
    } catch (error) {
      console.error('Error generating receipt URL:', error);
      return null;
    }
  };

  // Handler for sending receipt via text message
  const handleSendReceiptText = async () => {
    if (!selectedPayment || !selectedBusiness) return;
    
    try {
      setIsSendingText(true);
      setSendOptionsOpen(false);
      
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      // Get customer phone from the booking
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('customer_id')
        .eq('id', selectedPayment.booking_id)
        .single();
      
      if (bookingError || !bookingData) {
        console.error('Booking error:', bookingError);
        throw new Error('Could not find booking information');
      }
      
      // Get the customer's phone number
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('phone')
        .eq('id', bookingData.customer_id)
        .single();
      
      if (customerError || !customerData?.phone) {
        console.error('Customer error:', customerError);
        throw new Error('Could not find customer phone number');
      }
      
      // Generate the receipt URL
      const receiptUrl = await generateReceiptUrl();
      
      if (!receiptUrl) {
        throw new Error('Failed to generate receipt URL');
      }
      
      // Send the text message with the receipt link
      const businessName = selectedBusiness.name || 'Auto Service';
      const message = `${businessName} has sent you ${isPending ? 'an invoice' : 'a receipt'}. View it here: ${receiptUrl}`;
      
      const result = await sendCustomMessage(
        selectedBusiness.id,
        customerData.phone,
        message
      );
      
      if (!result.success) {
        console.error('SMS error:', result.error);
        throw new Error(result.error || 'Failed to send SMS');
      }
      
      // Show success feedback
      setTextSentSuccess(true);
      setTimeout(() => setTextSentSuccess(false), 3000);
      
    } catch (err) {
      console.error('Error sending receipt text:', err);
      setError(err instanceof Error ? err.message : 'Failed to send receipt text');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsSendingText(false);
    }
  };

  // Handler for sending receipt via email
  const handleSendReceiptEmail = async () => {
    if (!selectedPayment || !selectedBusiness) return;
    
    try {
      setIsSendingEmail(true);
      setSendOptionsOpen(false);
      
      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }
      
      // Get customer email from the payment details
      const customerEmail = selectedPayment.customer_email;
      
      if (!customerEmail) {
        throw new Error('Customer email not found');
      }
      
      // Generate the receipt URL for inclusion in email
      const receiptUrl = await generateReceiptUrl();
      
      if (!receiptUrl) {
        throw new Error('Failed to generate receipt URL');
      }
      
      // Prepare email content
      const businessName = selectedBusiness.name || 'Auto Service';
      const customerName = `${selectedPayment.customer_first_name} ${selectedPayment.customer_last_name}`;
      const documentType = isPending ? 'Invoice' : 'Receipt';
      const documentAction = isPending ? 'invoice' : 'receipt';
      
      const subject = `${documentType} #${selectedPayment.booking_number} from ${businessName}`;
      
      const emailBody = `Dear ${customerName},

Thank you for choosing ${businessName}! 

${isPending 
  ? `Please find your invoice #${selectedPayment.booking_number} for the amount of $${selectedPayment.amount.toLocaleString()}.`
  : `Please find your receipt #${selectedPayment.booking_number} for your recent payment of $${selectedPayment.amount.toLocaleString()}.`
}

${!isPending && selectedPayment.tip_amount && selectedPayment.tip_amount > 0 
  ? `Your payment included a tip of $${selectedPayment.tip_amount.toFixed(2)}. Thank you for your generosity!`
  : ''
}

You can view and download your ${documentAction} here: ${receiptUrl}

${isPending 
  ? 'Please process payment at your earliest convenience. If you have any questions about this invoice, please don\'t hesitate to contact us.'
  : 'Thank you for your business! If you have any questions about this receipt, please don\'t hesitate to contact us.'
}

Best regards,
${businessName}
${selectedBusiness.phone ? `Phone: ${selectedBusiness.phone}` : ''}
${selectedBusiness.email ? `Email: ${selectedBusiness.email}` : ''}`;
      
      // Send the email — Gmail → verified SendGrid → platform default sender.
      // The SendGrid path renders the body as HTML, so keep the line breaks.
      const result = await sendEmailWithFallback({
        businessId: selectedBusiness.id,
        to: customerEmail,
        subject: subject,
        body: emailBody.replace(/\n/g, '<br>')
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to send email');
      }
      
      // Show success feedback
      setEmailSentSuccess(true);
      setTimeout(() => setEmailSentSuccess(false), 3000);
      
    } catch (err) {
      console.error('Error sending receipt email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send receipt email');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!selectedPayment) return null;

  // Don't render anything if not open and not animating
  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-gray-900/50 z-[100000] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          left: 'env(safe-area-inset-left, 0px)',
          right: 'env(safe-area-inset-right, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 border-b border-[#2f2f2f] flex items-center justify-between bg-[#1a1a1a]">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
                <X className="h-5 w-5" />
              </button>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-base sm:text-lg font-semibold text-white">Payment Details</h2>
                  <span className="text-xs sm:text-sm text-gray-400">#{selectedPayment.booking_number}</span>
                </div>
                <div className="flex items-center mt-0.5">
                  <div className={`h-1.5 w-1.5 rounded-full ${isPending || selectedPayment.status === 'voided' ? 'bg-yellow-500' : 'bg-green-500'} mr-2`} />
                  <span className="text-xs sm:text-sm text-gray-400">
                    {isPending ? 'Invoice Unpaid' : selectedPayment.status === 'voided' ? 'Payment Voided - Needs Payment' : 'Payment Completed'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="relative" ref={sendMenuRef}>
                <button 
                  onClick={() => setSendOptionsOpen(!sendOptionsOpen)}
                  className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-white rounded-md transition-all duration-300 ${
                    textSentSuccess || emailSentSuccess 
                      ? 'bg-green-600 hover:bg-green-700 animate-pulse shadow-lg shadow-green-500/25' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <Send className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline-block">Send {isPending ? 'Invoice' : 'Receipt'}</span>
                  <ChevronDown className="h-3 w-3 sm:ml-1" />
                </button>
                {sendOptionsOpen && (
                  <div className="absolute top-10 right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      <button
                        onClick={handleSendReceiptText}
                        disabled={isSendingText}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        role="menuitem"
                      >
                        <div className="flex items-center">
                          <Phone className="w-3.5 h-3.5 mr-2 text-gray-500" />
                          {isSendingText ? 'Sending...' : `Text ${isPending ? 'Invoice' : 'Receipt'}`}
                        </div>
                      </button>
                      <button
                        onClick={handleSendReceiptEmail}
                        disabled={isSendingEmail}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        role="menuitem"
                      >
                        <div className="flex items-center">
                          <Mail className="w-3.5 h-3.5 mr-2 text-gray-500" />
                          {isSendingEmail ? 'Sending...' : `Email ${isPending ? 'Invoice' : 'Receipt'}`}
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {selectedPayment.status === 'completed' && (
                <button 
                  onClick={() => setIsVoidModalOpen(true)}
                  className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  <span className="hidden sm:inline-block">Void</span> Payment
                </button>
              )}
              <button 
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-gray-400 bg-[#2f2f2f] border border-[#3f3f3f] rounded-md hover:bg-[#2a2a2a] disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline-block">{isDownloading ? 'Downloading...' : 'Download Receipt'}</span>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-100">
            <div className="max-w-4xl mx-auto my-2 sm:my-8">
              {selectedBusiness && (
                <InvoiceDisplay 
                  payment={selectedPayment}
                  business={selectedBusiness}
                  forwardRef={invoiceRef}
                />
              )}

              {/* Invoice History Section */}
              {localAllPayments.length > 1 && (
                <div className="mt-4 bg-white border-t border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <button
                    onClick={() => setShowInvoiceHistory(!showInvoiceHistory)}
                    className="w-full px-3 sm:px-8 py-3 sm:py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">Invoice History</span>
                      <span className="text-xs text-gray-500">({localAllPayments.length} invoices)</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showInvoiceHistory ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showInvoiceHistory && (
                    <div className="border-t border-gray-200 px-3 sm:px-8 py-3 sm:py-4 bg-gray-50">
                      <div className="space-y-2">
                        {localAllPayments
                          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                          .map((payment, index) => {
                            const isSelected = selectedPayment?.id === payment.id;
                            const paymentIsPending = payment.status === 'pending' || payment.id === 'pending';
                            
                            return (
                              <div
                                key={payment.id}
                                className={`w-full p-3 rounded-lg border text-left transition-all hover:shadow-sm ${
                                  isSelected 
                                    ? 'border-blue-200 bg-blue-50 ring-1 ring-blue-200' 
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex flex-col">
                                      <div className="flex items-center space-x-2">
                                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-xs text-gray-600">
                                          {new Date(payment.created_at).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </span>
                                        {index === 0 && (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                            Latest
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center space-x-2 mt-1">
                                        <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-900">
                                          ${payment.amount.toLocaleString()}
                                          {payment.tip_amount && payment.tip_amount > 0 && (
                                            <span className="text-xs text-green-600 ml-1">
                                              (includes ${payment.tip_amount.toFixed(2)} tip)
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center space-x-2">
                                    {/* PDF Button - prioritized */}
                                    {payment.pdf_url ? (
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          window.open(payment.pdf_url, '_blank');
                                        }}
                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 transition-colors"
                                        title="Open PDF receipt"
                                      >
                                        <FileText className="h-3 w-3" />
                                        PDF
                                      </button>
                                    ) : (
                                      <span className="text-xs text-gray-400 px-2 py-1">
                                        No PDF
                                      </span>
                                    )}
                                    
                                    {/* View Details Button */}
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedPayment(payment);
                                      }}
                                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                                        isSelected 
                                          ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                      }`}
                                      title="View invoice details"
                                    >
                                      <Eye className="h-3 w-3" />
                                      {isSelected ? 'Viewing' : 'Details'}
                                    </button>
                                    
                                    {/* Status Badge */}
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      payment.status === 'completed'
                                        ? 'bg-green-100 text-green-700'
                                        : payment.status === 'voided'
                                        ? 'bg-red-100 text-red-700'
                                        : paymentIsPending
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}>
                                      {payment.status === 'completed' ? 'Paid' : 
                                       payment.status === 'voided' ? 'Voided' :
                                       paymentIsPending ? 'Unpaid' : payment.status}
                                    </span>
                                  </div>
                                </div>
                                
                                {payment.notes && payment.status === 'voided' && (
                                  <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                                    <span className="font-medium">Void reason:</span> {payment.notes}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                        💡 Click "PDF" to open receipts directly, or "Details" to view and send invoices
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Success/Error Notifications */}
          {(textSentSuccess || emailSentSuccess || error) && (
            <div className="fixed bottom-4 left-4 right-4 z-[100002] max-w-md mx-auto">
              {textSentSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-600 shadow-lg">
                  {isPending ? 'Invoice' : 'Receipt'} text message sent successfully!
                </div>
              )}
              
              {emailSentSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-600 shadow-lg">
                  {isPending ? 'Invoice' : 'Receipt'} email sent successfully!
                </div>
              )}
              
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 shadow-lg">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Void Payment Modal */}
      {isVoidModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100001] flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Void Payment</h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
              Are you sure you want to void this payment? This action cannot be undone.
            </p>
            
            <div className="mb-3 sm:mb-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Reason for voiding
              </label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                placeholder="Enter reason for voiding payment..."
              />
            </div>

            {error && (
              <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs sm:text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-2 sm:space-x-3">
              <button
                onClick={() => {
                  setIsVoidModalOpen(false);
                  setVoidReason('');
                  setError(null);
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedPayment) return;
                  
                  try {
                    setLoading(true);
                    setError(null);
                    
                    if (!voidReason.trim()) {
                      throw new Error('Please provide a reason for voiding the payment');
                    }
                    
                    await voidPayment(selectedPayment.id, voidReason.trim());
                    
                    const voidedPaymentData = {
                      ...selectedPayment,
                      status: 'voided' as const,
                      notes: voidReason.trim()
                    };
                    
                    // Update the selected payment with voided status and reason
                    setSelectedPayment(voidedPaymentData);
                    
                    // Update the invoice history (allPayments array) with voided status
                    setLocalAllPayments(prev => 
                      prev.map(payment => 
                        payment.id === selectedPayment.id 
                          ? voidedPaymentData
                          : payment
                      )
                    );
                    
                    await onUpdate();
                    
                    setIsVoidModalOpen(false);
                    setVoidReason('');
                  } catch (err) {
                    console.error('Error voiding payment:', err);
                    setError(err instanceof Error ? err.message : 'Failed to void payment');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !voidReason.trim()}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Voiding...' : 'Void Payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Use createPortal to render directly to document.body
  // This ensures the modal isn't constrained by parent containers
  return createPortal(modalContent, document.body);
}